/**
 * ContentActionsContent
 *
 * Content component for content action sheet.
 * Displays a list of actions that can be performed on a piece of content.
 *
 * Matches Flutter implementation: lib/presentation/bottom_sheet/content_actions_bottom_sheet.dart
 */

import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { ContentActionEntity } from '@/domain/entities/contentAction/contentActionEntity';
import { ContentActionType } from '@/domain/entities/contentAction/contentActionEntity';
import { useContentActions } from '../hooks/useContentActions';
import { ReportContentSheet } from '../../reportContent/ReportContentSheet';
import { SearchUsersSheet } from '../../searchUsers/SearchUsersSheet';
import { ShareSocialSheet } from '../../shareSocial/ShareSocialSheet';
import { ShareType } from '@/domain/enums/shareType';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { LinkContentSheet } from '@/presentation/feature/content/components/sheets/linkContent/LinkContentSheet';
import { AppealContentReportSheet } from '@/presentation/feature/content/components/sheets/appealContentReport/AppealContentReportSheet';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ContentActionsContentProps {
  media: UserMediaEntity;
  screenKey?: string;
}

export const ContentActionsContent: React.FC<ContentActionsContentProps> = ({
  media,
  screenKey,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();
  const responsive = useResponsiveDesign();

  const {
    isCurrentUser,
    contentIsSaved,
    generateShareData,
    saveToWatchLater,
    hideContent,
    deleteContent,
    publishDraft,
    publishScheduled,
    linkContent,
    unlinkContent,
  } = useContentActions({ media });

  const handleShareWithFriends = () => {
    const shareInfo: ShareInfoEntity = {
      type: ShareType.content,
      shareLink: media.shareLink,
      usersInfo: {
        userId: media.firebaseId ?? '',
        name: media.userName ?? '',
      },
      mediaInfo: {
        firebaseId: media.firebaseId ?? '',
        path: media.relativePath,
        thumbnail: media.relativeThumbnailPath ?? '',
        duration: media.duration,
        type: media.type,
        aspectRatio: media.aspectRatio,
        allowUnlock: media.allowUnlock,
      },
    };

    SearchUsersSheet.show(shareInfo, null, screenKey, responsive.searchUsersSheet.width);
  };

  const handleSocialShare = async () => {
    const thumbnailUrl = MediaUtils.getMediaThumbnailUrl(media);
    const userName = media.userName ?? '';
    const shareContent = await generateShareData();

    const socialShareProps = {
      headerImageUrl: thumbnailUrl,
      headerImageAlt: userName,
      shareTitle: shareContent.title,
      shareText: shareContent.text,
      shareUrl: shareContent.shareUrl,
    };

    if (navigator.share) {
      try {
        // Open a protective sheet so tapping outside the native share modal
        // doesn't close the underlying content actions sheet.
        SheetService.show({
          title: '',
          content: <div />,
          container: null,
          screenKey: screenKey,
          fixedHeight: '0vh',
          width: '0vw',
          closeOnClickOutside: false,
        });

        const shareData: ShareData = shareContent.file
          ? { title: shareContent.title, text: `${shareContent.text}\n\n${shareContent.shareUrl}`, files: [shareContent.file] }
          : { title: shareContent.title, text: shareContent.text, url: shareContent.shareUrl };

        await navigator.share(shareData);
        SheetService.close();
      } catch (error) {
        SheetService.close();
        if (error instanceof Error) {
          if (error.message === 'Share canceled' || error.name === 'AbortError') {
            return;
          }
          console.error('Error sharing:', error);
          ShareSocialSheet.show(socialShareProps, null, screenKey);
        }
      }
    } else {
      ShareSocialSheet.show(socialShareProps, null, screenKey);
    }
  };

  const handleSaveToWatchLater = async () => {
    const success = await saveToWatchLater();
    if (!success) toast.error(l10n.common.genericError);
  };

  const handleHideContent = async () => {
    let hideSucceeded = false;
    const response = await ConfirmSheet.show({
      title: l10n.contentActions.hideContentTitle,
      confirmText: l10n.contentActions.hideContentDescription,
      okText: l10n.contentActions.hideContentConfirm,
      cancelText: l10n.common.cancel,
      screenKey,
      onConfirm: async () => {
        const success = await hideContent();
        if (!success) toast.error(l10n.common.genericError);
        else hideSucceeded = true;
      },
      loadingStyle: 'button',
    });

    // Close the content actions sheet only after a successful hide (confirm sheet closes automatically)
    if (response && hideSucceeded) {
      SheetService.close();
    }
  };

  const handlePublishDraft = async () => {
    SheetService.close();
    const success = await publishDraft();
    if (!success) toast.error(l10n.contentActions.publishDraftError);
    else toast.success(l10n.contentActions.publishDraftSuccess);
  };

  const handlePublishScheduled = async () => {
    if (media.schedule == null) return;
    const formattedDate = dateFormatters.formatDate(media.schedule, true);
    const description = l10n.contentActions.scheduledContentDescription.replace('{date}', formattedDate);

    let publishSucceeded = false;
    const response = await ConfirmSheet.show({
      title: l10n.contentActions.scheduledContentTitle,
      confirmText: description,
      okText: l10n.contentActions.publishNow,
      cancelText: l10n.common.close,
      screenKey,
      onConfirm: async () => {
        const success = await publishScheduled();
        if (success) publishSucceeded = true;
        else toast.error(l10n.contentActions.publishScheduledError);
      },
      loadingStyle: 'button',
    });

    if (response && publishSucceeded) {
      toast.success(l10n.contentActions.publishScheduledSuccess);
      SheetService.close();
    }
  };

  const handleDeleteContent = async () => {
    const isPhoto = media.type === 0;
    const title = isPhoto ? l10n.contentActions.deletePhotoTitle : l10n.contentActions.deleteVideoTitle;

    let deleteSucceeded = false;
    const response = await ConfirmSheet.show({
      title,
      confirmText: l10n.contentActions.deleteContentDescription,
      okText: l10n.contentActions.deleteContentConfirm,
      cancelText: l10n.common.cancel,
      screenKey,
      onConfirm: async () => {
        const success = await deleteContent();
        if (success) deleteSucceeded = true;
        else toast.error(l10n.contentActions.deleteContentError);
      },
      loadingStyle: 'button',
    });

    if (response && deleteSucceeded) {
      SheetService.close();
    }
  };

  const handleLinkContent = async () => {
    const result = await LinkContentSheet.show({
      title: l10n.publish.linkContentSheetTitle,
      excludeMediaId: media.id,
      screenKey,
    });
    if (!result) return;

    const success = await linkContent(result);
    if (!success) {
      toast.error(l10n.contentActions.linkError);
      return;
    }
    toast.success(l10n.contentActions.linkSuccess);
    SheetService.close();
  };

  const handleUnlinkContent = async () => {
    let unlinkSucceeded = false;
    const response = await ConfirmSheet.show({
      title: l10n.contentActions.unlinkContentTitle,
      confirmText: l10n.contentActions.unlinkContentDescription,
      okText: l10n.contentActions.unlinkContentConfirm,
      cancelText: l10n.common.cancel,
      screenKey,
      onConfirm: async () => {
        const success = await unlinkContent();
        if (success) unlinkSucceeded = true;
        else toast.error(l10n.contentActions.unlinkError);
      },
      loadingStyle: 'button',
    });

    if (response && unlinkSucceeded) {
      toast.success(l10n.contentActions.unlinkSuccess);
      SheetService.close();
    }
  };

  const handleAppealContent = async () => {
    const submitted = await AppealContentReportSheet.show({
      contentId: media.id,
      screenKey,
    });
    if (submitted) {
      SheetService.close();
    }
  };

  const handleActionClick = (action: ContentActionEntity) => {
    switch (action.type) {
      case ContentActionType.SHARE:
        handleShareWithFriends();
        break;
      case ContentActionType.REPORT:
        ReportContentSheet.show(media, undefined, screenKey, responsive.reportContentSheet.fixedHeight);
        break;
      case ContentActionType.SHARE_SOCIAL:
        handleSocialShare();
        break;
      case ContentActionType.SAVE:
        handleSaveToWatchLater();
        break;
      case ContentActionType.HIDE:
        handleHideContent();
        break;
      case ContentActionType.PUBLISH_DRAFT:
        handlePublishDraft();
        break;
      case ContentActionType.SCHEDULE_CONTENT:
        handlePublishScheduled();
        break;
      case ContentActionType.DELETE:
        handleDeleteContent();
        break;
      case ContentActionType.LINK:
        handleLinkContent();
        break;
      case ContentActionType.UNLINK:
        handleUnlinkContent();
        break;
      case ContentActionType.APPEAL:
        handleAppealContent();
        break;
      case ContentActionType.PROCESSING:
        // No-op (matches Flutter)
        break;
    }
  };

  // Define action builders - must be before conditional returns to maintain hook order
  const getCurrentUserActions = (): ContentActionEntity[] => {
    const result: ContentActionEntity[] = [];

    if (media.isProcessing) {
      result.push({
        image: '/assets/images/settings.png',
        name: l10n.contentActions.processing,
        type: ContentActionType.PROCESSING,
      });
      return result;
    }

    if (media.blocked) {
      result.push({
        image: '/assets/images/delete.png',
        name: l10n.contentActions.delete,
        type: ContentActionType.DELETE,
      });

      result.push({
        image: '/assets/images/appeal.png',
        name: l10n.contentActions.appealBlockedContent,
        type: ContentActionType.APPEAL,
      });
      return result;
    }

    result.push({
      image: '/assets/images/share.png',
      name: l10n.contentActions.shareWithFriends,
      type: ContentActionType.SHARE,
    });

    result.push({
      image: '/assets/images/share_social.png',
      name: l10n.contentActions.shareOutsideExpause,
      type: ContentActionType.SHARE_SOCIAL,
    });

    if (media.schedule != null) {
      result.push({
        image: '/assets/images/timer.png',
        name: l10n.contentActions.publishScheduledContent,
        type: ContentActionType.SCHEDULE_CONTENT,
      });
    }

    if (media.isDraft) {
      result.push({
        image: '/assets/images/draft.png',
        name: l10n.contentActions.publishDraft,
        type: ContentActionType.PUBLISH_DRAFT,
      });
    } else {
      result.push({
        image: '/assets/images/link.png',
        name: media.contentLink == null ? l10n.contentActions.linkContent : l10n.contentActions.unlinkContent,
        type: media.contentLink == null ? ContentActionType.LINK : ContentActionType.UNLINK,
      });
    }

    result.push({
      image: '/assets/images/delete.png',
      name: l10n.contentActions.delete,
      type: ContentActionType.DELETE,
    });

    return result;
  };

  const getPeerUserActions = (): ContentActionEntity[] => {
    const result: ContentActionEntity[] = [];

    result.push({
      image: '/assets/images/share.png',
      name: l10n.contentActions.shareWithFriends,
      type: ContentActionType.SHARE,
    });

    result.push({
      image: '/assets/images/share_social.png',
      name: l10n.contentActions.shareOutsideExpause,
      type: ContentActionType.SHARE_SOCIAL,
    });

    result.push({
      image: '/assets/images/saved.png',
      name: contentIsSaved ? l10n.contentActions.removeFromWatchLater : l10n.contentActions.saveToWatchLater,
      type: ContentActionType.SAVE,
    });

    result.push({
      image: '/assets/images/hide.png',
      name: l10n.contentActions.hide,
      type: ContentActionType.HIDE,
    });

    result.push({
      image: '/assets/images/report.png',
      name: l10n.contentActions.report,
      type: ContentActionType.REPORT,
    });

    return result;
  };

  // Calculate actions - useMemo must be called before conditional returns
  const contentActions = useMemo(() => {
    return isCurrentUser ? getCurrentUserActions() : getPeerUserActions();
  }, [isCurrentUser, contentIsSaved, media, l10n]);

  return (
    <div
      style={{
        paddingLeft: responsive.spacing['2xl'],
        paddingRight: responsive.spacing['2xl'],
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {contentActions.map((action) => (
        <div
          key={action.type}
          {...qaAttr({ id: `content-action-sheet-${action.type}` })}
          style={{
            marginBottom: responsive.spacing['2xl'],
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => handleActionClick(action)}
        >
          <div
            style={{
              width: responsive.action.imageSize,
              height: responsive.action.imageSize,
              borderRadius: responsive.action.imageSize,
              backgroundColor: theme.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <AssetIcon path={action.image} width={responsive.action.imageSize / 2} height={responsive.action.imageSize / 2} />
          </div>
          <div
            style={{
              marginLeft: responsive.spacing.sm,
              flex: 1,
            }}
          >
            <span
              style={{
                color: theme.text.primary,
                fontSize: responsive.font.base,
              }}
            >
              {action.name}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
